import React, { useState, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  IconButton,
  SimpleGrid,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  FiCalendar,
  FiMenu,
  FiZoomIn,
  FiZoomOut,
  FiSearch,
  FiMousePointer,
  FiHome,
  FiChevronDown,
} from "react-icons/fi";

/* -------- Dummy Data (Keeping original structure) -------- */
const depositWithdrawData = [
  { date: "28-December-2025", deposited: 0, withdrawn: 0 },
  { date: "29-December-2025", deposited: 0, withdrawn: 0 },
  { date: "30-December-2025", deposited: 0, withdrawn: 0 },
  { date: "31-December-2025", deposited: 0, withdrawn: 0 },
  { date: "01-January-2026", deposited: 0, withdrawn: 0 },
  { date: "02-January-2026", deposited: 0, withdrawn: 0 },
  { date: "03-January-2026", deposited: 0, withdrawn: 0 },
  { date: "04-January-2026", deposited: 0, withdrawn: 0 },
  { date: "05-January-2026", deposited: 0, withdrawn: 0 },
  { date: "06-January-2026", deposited: 0, withdrawn: 0 },
  { date: "07-January-2026", deposited: 0, withdrawn: 0 },
  { date: "08-January-2026", deposited: 0, withdrawn: 0 },
  { date: "09-January-2026", deposited: 0, withdrawn: 0 },
  { date: "10-January-2026", deposited: 0, withdrawn: 0 },
  { date: "11-January-2026", deposited: 0, withdrawn: 0 },
];

const transactionData = [
  { date: "28-December-2025", plus: 0, minus: 0 },
  { date: "29-December-2025", plus: 0, minus: 0 },
  { date: "30-December-2025", plus: 0, minus: 0 },
  { date: "31-December-2025", plus: 0, minus: 0 },
  { date: "01-January-2026", plus: 0, minus: 0 },
  { date: "02-January-2026", plus: 0, minus: 0 },
  { date: "03-January-2026", plus: 0, minus: 0 },
  { date: "04-January-2026", plus: 0, minus: 0 },
  { date: "05-January-2026", plus: 0, minus: 0 },
  { date: "06-January-2026", plus: 0, minus: 0 },
  { date: "07-January-2026", plus: 0, minus: 0 },
  { date: "08-January-2026", plus: 0, minus: 0 },
  { date: "09-January-2026", plus: 0, minus: 0 },
  { date: "10-January-2026", plus: 0, minus: 0 },
  { date: "11-January-2026", plus: 0, minus: 0 },
];

/* -------- Download Utils -------- */
const handleDownload = (format, chartRef, data, title) => {
  if (!chartRef.current) return;

  const fileName = title.toLowerCase().replace(/ /g, "_");

  if (format === "CSV") {
    const headers = Object.keys(data[0]).join(",");
    const rows = data.map((row) => Object.values(row).join(",")).join("\n");
    const csvContent = `data:text/csv;charset=utf-8,${headers}\n${rows}`;
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  const svgElement = chartRef.current.querySelector("svg");
  if (!svgElement) return;

  const svgData = new XMLSerializer().serializeToString(svgElement);
  const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });

  if (format === "SVG") {
    const url = URL.createObjectURL(svgBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } else if (format === "PNG") {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      canvas.width = img.width * 2;
      canvas.height = img.height * 2;
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const pngUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = pngUrl;
      link.download = `${fileName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }
};

/* -------- Custom Components -------- */

const CustomLegend = ({ items }) => {
  const labelColor = useColorModeValue("#64748b", "gray.400");
  return (
    <Flex justify="center" align="center" mt={4} gap={6} flexWrap="wrap">
      {items.map((item, index) => (
        <HStack key={index} spacing={2} align="center">
          <Box w="12px" h="12px" borderRadius="full" bg={item.color} />
          <Text fontSize="14px" color={labelColor} fontWeight="500">
            {item.label}
          </Text>
        </HStack>
      ))}
    </Flex>
  );
};

const DatePickerMenu = ({
  selectedRange,
  onRangeChange,
  showLabel = false,
}) => {
  const options = [
    "Today",
    "Yesterday",
    "Last 7 Days",
    "Last 15 Days",
    "Last 30 Days",
    "This Month",
    "Last Month",
    "Last 6 Months",
    "This Year",
    "Custom Range",
  ];

  const borderColor = useColorModeValue("#e2e8f0", "gray.600");
  const bg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("#64748b", "gray.300");
  const menuItemHoverBg = useColorModeValue("gray.50", "gray.700");
  const menuListBorderColor = useColorModeValue("#edf2f7", "gray.600");
  const buttonHoverBg = useColorModeValue("gray.50", "gray.700");
  const buttonActiveBg = useColorModeValue("gray.100", "gray.600");

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="outline"
        size="sm"
        borderColor={borderColor}
        bg={bg}
        fontWeight="500"
        color={textColor}
        rightIcon={<FiChevronDown />}
        leftIcon={<FiCalendar />}
        _hover={{ bg: buttonHoverBg }}
        _active={{ bg: buttonActiveBg }}
        px={3}
        height="36px"
        textAlign="left"
      >
        {showLabel ? (
          <Text fontSize="13px" isTruncated maxW="250px">
            {selectedRange === "Last 30 Days"
              ? "December 28, 2025 - January 11, 2026"
              : selectedRange}
          </Text>
        ) : (
          ""
        )}
      </MenuButton>
      <MenuList
        borderRadius="8px"
        boxShadow="0 4px 12px rgba(0,0,0,0.1)"
        border="1px solid"
        borderColor={menuListBorderColor}
        bg={bg}
        py={1}
        minW="200px"
        zIndex={10}
      >
        {options.map((option) => (
          <MenuItem
            key={option}
            onClick={() => onRangeChange(option)}
            bg={selectedRange === option ? "#4f46e5" : "transparent"}
            color={selectedRange === option ? "white" : textColor}
            fontSize="14px"
            fontWeight="500"
            _hover={{
              bg: selectedRange === option ? "#4338ca" : menuItemHoverBg,
            }}
            py={2}
          >
            {option}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

const DownloadMenu = ({ chartRef, data, title }) => {
  const bg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("#64748b", "gray.300");
  const hoverBg = useColorModeValue("gray.100", "gray.700");
  const borderColor = useColorModeValue("#edf2f7", "gray.600");
  const itemHoverBg = useColorModeValue("gray.50", "gray.700");

  return (
    <Menu>
      <MenuButton
        as={IconButton}
        icon={<FiMenu />}
        variant="ghost"
        size="sm"
        color={textColor}
        aria-label="Download Menu"
        _hover={{ bg: hoverBg }}
      />
      <MenuList
        borderRadius="8px"
        py={1}
        border="1px solid"
        borderColor={borderColor}
        bg={bg}
        zIndex={20}
      >
        <MenuItem
          fontSize="14px"
          onClick={() => handleDownload("SVG", chartRef, data, title)}
          _hover={{ bg: itemHoverBg }}
        >
          Download SVG
        </MenuItem>
        <MenuItem
          fontSize="14px"
          onClick={() => handleDownload("PNG", chartRef, data, title)}
          _hover={{ bg: itemHoverBg }}
        >
          Download PNG
        </MenuItem>
        <MenuItem
          fontSize="14px"
          onClick={() => handleDownload("CSV", chartRef, data, title)}
          _hover={{ bg: itemHoverBg }}
        >
          Download CSV
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

const Toolbar = ({
  type,
  selectedRange,
  onRangeChange,
  chartRef,
  data,
  title,
  onZoomIn,
  onZoomOut,
  onResetZoom,
}) => {
  const textColor = useColorModeValue("#64748b", "gray.300");
  const hoverBg = useColorModeValue("gray.100", "gray.700");

  if (type === "deposit") {
    return (
      <Flex direction="column" align="flex-end" gap={2}>
        <DatePickerMenu
          selectedRange={selectedRange}
          onRangeChange={onRangeChange}
        />
        <DownloadMenu chartRef={chartRef} data={data} title={title} />
      </Flex>
    );
  }

  return (
    <Flex direction="column" align="flex-end" gap={1}>
      <DatePickerMenu
        selectedRange={selectedRange}
        onRangeChange={onRangeChange}
        showLabel={true}
      />
      <HStack spacing={0} mt={1}>
        <IconButton
          icon={<FiZoomIn />}
          variant="ghost"
          size="xs"
          color={textColor}
          onClick={onZoomIn}
          title="Zoom In"
          _hover={{ bg: hoverBg }}
        />
        <IconButton
          icon={<FiZoomOut />}
          variant="ghost"
          size="xs"
          color={textColor}
          onClick={onZoomOut}
          title="Zoom Out"
          _hover={{ bg: hoverBg }}
        />
        <IconButton
          icon={<FiSearch />}
          variant="ghost"
          size="xs"
          color={textColor}
          _hover={{ bg: hoverBg }}
        />
        <IconButton
          icon={<FiMousePointer />}
          variant="ghost"
          size="xs"
          color={textColor}
          _hover={{ bg: hoverBg }}
        />
        <IconButton
          icon={<FiHome />}
          variant="ghost"
          size="xs"
          color={textColor}
          onClick={onResetZoom}
          title="Reset View"
          _hover={{ bg: hoverBg }}
        />
        <DownloadMenu chartRef={chartRef} data={data} title={title} />
      </HStack>
    </Flex>
  );
};

const ReportCard = ({
  title,
  type,
  children,
  legendItems,
  selectedRange,
  onRangeChange,
  chartRef,
  data,
  onZoomIn,
  onZoomOut,
  onResetZoom,
}) => {
  const bg = useColorModeValue("white", "gray.800");
  const titleColor = useColorModeValue("#334155", "white");

  return (
    <Box
      bg={bg}
      p={{ base: 4, md: 5 }}
      borderRadius="12px"
      boxShadow="0 4px 20px rgba(0,0,0,0.05)"
      height="100%"
    >
      <Flex justify="space-between" align="start" mb={6}>
        <Text fontSize="18px" fontWeight="600" color={titleColor}>
          {title}
        </Text>
        <Toolbar
          type={type}
          selectedRange={selectedRange}
          onRangeChange={onRangeChange}
          chartRef={chartRef}
          data={data}
          title={title}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          onResetZoom={onResetZoom}
        />
      </Flex>
      {children}
      <CustomLegend items={legendItems} />
    </Box>
  );
};

/* -------- Main Component -------- */
export default function ReportsSection() {
  const [range1, setRange1] = useState("Last 30 Days");
  const [range2, setRange2] = useState("Last 30 Days");

  const [zoomIndex, setZoomIndex] = useState({
    start: 0,
    end: transactionData.length,
  });

  const chartRef1 = useRef(null);
  const chartRef2 = useRef(null);

  const gridStroke = useColorModeValue("#f1f5f9", "gray.700");
  const tickColor = useColorModeValue("#64748b", "gray.400");
  const tooltipBg = useColorModeValue("white", "#1a202c");
  const tooltipColor = useColorModeValue("black", "white");

  const handleZoomIn = () => {
    const currentLength = zoomIndex.end - zoomIndex.start;
    if (currentLength <= 4) return;
    const mid = Math.floor((zoomIndex.start + zoomIndex.end) / 2);
    const newHalfRange = Math.max(2, Math.floor(currentLength / 2.8));
    setZoomIndex({
      start: Math.max(0, mid - newHalfRange),
      end: Math.min(transactionData.length, mid + newHalfRange),
    });
  };

  const handleZoomOut = () => {
    const currentLength = zoomIndex.end - zoomIndex.start;
    if (currentLength >= transactionData.length) return;
    const mid = Math.floor((zoomIndex.start + zoomIndex.end) / 2);
    const newHalfRange = Math.min(
      transactionData.length,
      Math.ceil(currentLength * 0.8),
    );
    setZoomIndex({
      start: Math.max(0, mid - newHalfRange),
      end: Math.min(transactionData.length, mid + newHalfRange),
    });
  };

  const handleResetZoom = () => {
    setZoomIndex({ start: 0, end: transactionData.length });
  };

  const filteredTransactionData = transactionData.slice(
    zoomIndex.start,
    zoomIndex.end,
  );

  return (
    <SimpleGrid
      columns={{ base: 1, xl: 2 }}
      spacing={6}
      mt={8}
      sx={{
        ".recharts-cartesian-grid-horizontal line, .recharts-cartesian-grid-vertical line":
          {
            stroke: gridStroke,
          },
        ".recharts-xAxis .recharts-cartesian-axis-tick-value": {
          fontSize: "11px",
          fill: tickColor,
        },
        ".recharts-yAxis .recharts-cartesian-axis-tick-value": {
          fontSize: "11px",
          fill: tickColor,
        },
      }}
    >
      {/* Deposit & Withdraw Report */}
      <ReportCard
        title="Deposit & Withdraw Report"
        type="deposit"
        selectedRange={range1}
        onRangeChange={setRange1}
        chartRef={chartRef1}
        data={depositWithdrawData}
        legendItems={[
          { label: "Deposited", color: "#10b981" },
          { label: "Withdrawn", color: "#ef4444" },
        ]}
      >
        <Box height="320px" width="100%" ref={chartRef1}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={depositWithdrawData}
              margin={{ top: 5, right: 20, left: 0, bottom: 50 }}
            >
              <CartesianGrid strokeDasharray="0" vertical={false} />
              <XAxis
                dataKey="date"
                angle={-45}
                textAnchor="end"
                axisLine={false}
                tickLine={false}
                interval={0}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                label={{
                  value: "₹",
                  angle: -90,
                  position: "insideLeft",
                  style: {
                    fill: "#3b82f6",
                    fontSize: "12px",
                    fontWeight: "bold",
                  },
                }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: "8px",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  backgroundColor: tooltipBg,
                  color: tooltipColor,
                }}
              />
              <Line
                type="monotone"
                dataKey="deposited"
                stroke="#10b981"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="withdrawn"
                stroke="#ef4444"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </ReportCard>

      {/* Transactions Report */}
      <ReportCard
        title="Transactions Report"
        type="transaction"
        selectedRange={range2}
        onRangeChange={setRange2}
        chartRef={chartRef2}
        data={transactionData}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetZoom={handleResetZoom}
        legendItems={[
          { label: "Plus Transactions", color: "#10b981" },
          { label: "Minus Transactions", color: "#ef4444" },
        ]}
      >
        <Box height="320px" width="100%" ref={chartRef2}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={filteredTransactionData}
              margin={{ top: 5, right: 20, left: 0, bottom: 50 }}
            >
              <CartesianGrid strokeDasharray="0" vertical={false} />
              <XAxis
                dataKey="date"
                angle={-45}
                textAnchor="end"
                axisLine={false}
                tickLine={false}
                interval={0}
              />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  borderRadius: "8px",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  backgroundColor: tooltipBg,
                  color: tooltipColor,
                }}
              />
              <Line
                type="monotone"
                dataKey="plus"
                stroke="#10b981"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="minus"
                stroke="#ef4444"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </ReportCard>
    </SimpleGrid>
  );
}
